/**
 * All guest-facing copy in one file.
 *
 * Kept here rather than inline in screens so the copy pass is one file and one
 * review, and so the web/kiosk differences are visible side by side. Where a
 * screen differs between modes by copy alone, that is the only thing that forks
 * — the component is shared.
 *
 * Sourced from Super Objective's storyboards (docs/[INT] SUMERIAN_The
 * Incomer-Seagull Callers.pdf). Two boards were still Lorem Ipsum — the kiosk
 * instruction heading and the kiosk caw prompt — and mirror the web copy, per
 * the deck's own notes that they are the same beats.
 *
 * The release date is NOT in this file at all — screens read `release.line`
 * from assets.ts directly, so the date changes in one place. The SMS bodies in
 * `supabase/functions/*` carry the same line and cannot import from src/; they
 * read a RELEASE_LINE secret that has to be set to match.
 *
 * Written in sentence case throughout; display sites apply `uppercase`.
 */

export const copy = {
  /**
   * Web step 1. Also the permission gate — the storyboard puts the camera and
   * mic rows on this page rather than on a screen of their own.
   */
  title: {
    heading: 'Do you have what it takes to become a Gull of Auk Isle?',
    cta: 'Allow camera and mic',
    /** Shown instead when a revisit already has both. */
    ctaReady: 'Start',
    cameraRow: 'Camera access',
    micRow: 'Mic access',
    rowPending: 'Required',
    rowGranted: 'Allowed',
    rowDenied: 'Blocked',
    /**
     * The way in for a kiosk guest who took a code instead of leaving a number.
     * The kiosk sends them to the bare host, so this screen is where they land,
     * and without this line the code has nowhere to go. Worded to match what
     * the kiosk told them — "code", "clip" — so it is recognisable as the thing
     * they were sent here to do.
     */
    codeLink: 'Got a code from the kiosk? Get your clip',
  },

  inAppBrowser: {
    heading: 'Not in here',
    bodyIOS:
      'This one needs your real browser. Tap the dots in the corner, choose Open in Safari, and come back.',
    bodyAndroid: 'This one needs your real browser. Open it in Chrome and come back.',
    ctaAndroid: 'Open in Chrome',
    ctaCopy: 'Copy link instead',
    ctaContinue: 'Continue anyway',
    copied: 'Link copied',
    stepsHeading: 'Where to tap',
    stepDots: 'The ••• button, usually top right.',
    stepOpen: 'Open in browser / Open in Safari.',
  },

  /**
   * Only the refusal survives as a screen of its own — the ask itself moved on
   * to the title page.
   */
  permission: {
    asking: '…',
    denied: {
      heading: 'The camera is blocked',
      body: 'The camera permission was refused. Reload and allow it, or open your browser settings for this site.',
      cta: 'Try again',
    },
    micDenied: 'No microphone. Carrying on — your clip will be silent.',
  },

  instruction: {
    heading: 'Learn the Caw of the Wild',
    cta: 'Tap to start',
  },

  camera: {
    /** One prompt for both modes; the sub-lines are where the fork belongs. */
    prompt: 'Open your mouth.',
    promptLine2: 'Unleash your best caw.',
    subWeb: "(Don't be shy)",
    subKiosk: "(Don't be shy)",
    opening: 'Opening the camera…',
    /** Shown when nothing has triggered for a while. */
    hint: 'Wider. Let it out.',
    /** The way through when detection is not playing along. */
    fallbackCta: 'Change me anyway',
  },

  /*
   * No copy over the transformation wipe, deliberately. The caption that used
   * to live here covered the middle of the frame through the transformation
   * itself — see CaptureScreen's transition phase.
   */

  reveal: {
    heading: 'Welcome to the family!',
    sub: 'You are now an official Gull of Auk Isle',
  },

  result: {
    heading: 'Retake or Keep?',
    sub: 'Ready to share with the world?',
    /** Kiosk cannot share from the panel — it offers to text it instead. */
    headingKiosk: 'Share or save',
    subKiosk: 'Retake?',
    save: 'Save',
    /** One label for both modes — a guest is redoing a take, not replaying. */
    retake: 'Retake',
    sendToPhone: 'Looks good!',
    saved: 'Saved',
    /**
     * Only shown when the clip actually went to the browser's downloads. The
     * previous line here told guests to press and hold the video, which is
     * advice for an image — a long press on a blob-backed <video> offers no
     * save at all, so it sent people to a dead end.
     */
    savedToDownloads: 'Saved to your downloads.',
    /** The recorder produced nothing — shown in place of the clip. */
    noClip: 'The recording did not come through. Have another go.',
  },

  phone: {
    heading: "Enter your phone number and we'll text you when it's ready",
    placeholder: '555-555-5555',
    consent:
      'Text me my clip and news about The Incomer. Message and data rates may apply. Reply STOP to opt out.',
    cta: 'Submit',
    delete: 'Del',
    or: '— or —',
    /** The host is rendered between these two halves. */
    codeInstruction: 'Go to',
    codeInstructionTail: 'and enter this code to get your clip.',
    useCode: 'I have written it down',
    invalid: 'That does not look like a mobile number.',
    needConsent: 'Tick the box and we can send it.',
    sending: 'Sending…',
    queued: 'On its way.',
  },

  endCard: {
    headingWeb: 'The Incomer',
    headingKiosk: 'Check your phone',
    tickets: 'Get tickets now',
    again: 'Again',
    kioskFooter: 'Thanks for calling.',
  },

  /** /c — redeeming a code copied down at the kiosk. */
  codeEntry: {
    heading: 'Enter your code',
    body: 'The six characters from the kiosk screen.',
    placeholder: 'ABC123',
    cta: 'Get my clip',
  },

  /** The retrieval page the SMS links to — /clip/<session id>. */
  clip: {
    /** They may open this days later, from a text, with no other context. */
    readyHeading: 'Yours to keep',
    save: 'Save it',
    saving: 'Getting it…',
    /** iOS puts a direct download in Files, not Photos. Say where to look. */
    savedNote: 'Saved to your downloads.',
    retry: 'Check again',

    loadingHeading: 'Finding your clip',
    loadingBody: 'One moment.',

    pendingHeading: 'Not quite ready',
    pendingBody:
      'Your clip is still being prepared. Give it a minute and check again — the link will keep working.',

    failedHeading: 'That one got away',
    failedBody:
      'Something went wrong preparing your clip and we could not recover it. Sorry — that one is on us.',

    errorHeading: 'Could not load it',
    errorBody: 'Something went wrong fetching your clip. Check your connection and try again.',

    /**
     * Only on the code path. A mistyped code looks exactly like a clip that has
     * not finished uploading — both are "no row" — so the page cannot tell a
     * guest which one they are looking at. Rather than guess, it offers the one
     * move that fixes the case it cannot detect.
     */
    wrongCode: 'Enter a different code',
  },

  /**
   * Kiosk step 1. The storyboard board carries the touch line and nothing else —
   * the attract footage is doing the work, and the previous heading here
   * ("Become the Incomer") had the character logic backwards: the costume
   * belongs to the siblings, who wear it to repel incomers.
   */
  attract: {
    cta: 'Touch Screen to Start',
  },

  error: {
    heading: 'Something went wrong',
    body: 'That did not start properly. Try once more.',
    cta: 'Try again',
  },
} as const;
